import React, { useState, useEffect, useRef } from "react";
import Modal from "components/modal";
import { MdCloudUpload, MdDelete, MdPerson } from "react-icons/md";

const UserFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialUser, 
  isEditing,
  roles = [],
  promotions = [],
  loading = false,
  rolesLoading = false,
  promotionsLoading = false
}) => {
  const [user, setUser] = useState({
    nom_prenom_membre: "",
    id_prom: "",
    role_id: "",
    tel_membre: "",
    com_origin: "",
    num_matricule: "",
    adresse_membre: "",
    date_naissance: new Date().toISOString().split('T')[0],
    username: "",
    cin_membre: "",
    password: "",
    email: "",
    photo_profile: null
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && initialUser) {
      setUser({
        nom_prenom_membre: initialUser.nom_prenom_membre || "",
        id_prom: initialUser.id_prom || "",
        role_id: initialUser.role_id || "",
        tel_membre: initialUser.tel_membre || "",
        com_origin: initialUser.com_origin || "",
        num_matricule: initialUser.num_matricule || "",
        adresse_membre: initialUser.adresse_membre || "",
        date_naissance: initialUser.date_naissance || new Date().toISOString().split('T')[0],
        username: initialUser.username || "",
        cin_membre: initialUser.cin_membre || "",
        password: initialUser.password || "",
        email: initialUser.email || "",
        photo_profile: null
      });
      setFileName(initialUser.photo || "");
      setValidationErrors({});
    }
  }, [isOpen, initialUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });

    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null,
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier le type de fichier (images uniquement)
      if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/)) {
        setValidationErrors({
          ...validationErrors,
          photo_profile: "Seules les images sont acceptées (JPEG, PNG, GIF, WebP)"
        });
        return;
      }
      
      // Vérifier la taille du fichier (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setValidationErrors({
          ...validationErrors,
          photo_profile: "L'image est trop volumineuse (max 2MB)"
        });
        return;
      }
      
      setUser({ ...user, photo_profile: file });
      setFileName(file.name);
      setValidationErrors({
        ...validationErrors,
        photo_profile: null,
      });
    }
  };

  const removeFile = () => {
    setUser({ ...user, photo_profile: null });
    setFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!user.nom_prenom_membre.trim()) {
      errors.nom_prenom_membre = "Le nom et prénom sont requis";
    }

    if (!user.username.trim()) {
      errors.username = "Le nom d'utilisateur est requis";
    } else if (!/^[a-zA-Z0-9_]{3,20}$/.test(user.username)) {
      errors.username = "Le nom d'utilisateur doit contenir entre 3 et 20 caractères alphanumériques";
    }

    if (!user.email.trim()) {
      errors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
      errors.email = "L'adresse email n'est pas valide";
    }

    if (!isEditing && !user.password.trim()) {
      errors.password = "Le mot de passe est requis pour un nouvel utilisateur";
    } else if (user.password && user.password.length < 6) {
      errors.password = "Le mot de passe doit contenir au moins 6 caractères";
    }

    if (!user.tel_membre.trim()) {
      errors.tel_membre = "Le numéro de téléphone est requis";
    }

    if (!user.com_origin.trim()) {
      errors.com_origin = "La commune d'origine est requise";
    }

    if (!user.role_id) {
      errors.role_id = "Le rôle est requis";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    
    const result = await onSubmit(user);
    
    if (!result.success) {
      setValidationErrors(result.errors || {});
    }
    
    setSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="lg">
      <div className="text-center sm:text-left">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            {isEditing
              ? "Modifier l'utilisateur"
              : "Créer un nouvel utilisateur"}
          </h3>
          <div className="h-1 w-12 bg-indigo-600 mt-2 rounded-full"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="nom_prenom_membre"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Nom et prénom *
                  </label>
                  <input
                    type="text"
                    name="nom_prenom_membre"
                    id="nom_prenom_membre"
                    value={user.nom_prenom_membre}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: John Doe"
                  />
                  {validationErrors.nom_prenom_membre && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {validationErrors.nom_prenom_membre}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="tel_membre"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    name="tel_membre"
                    id="tel_membre"
                    value={user.tel_membre}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: +261 34 56 789 01"
                  />
                  {validationErrors.tel_membre && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {validationErrors.tel_membre}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="com_origin"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Commune d'origine *
                  </label>
                  <input
                    type="text"
                    name="com_origin"
                    id="com_origin"
                    value={user.com_origin}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: Ambalavao"
                  />
                  {validationErrors.com_origin && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {validationErrors.com_origin}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="adresse_membre"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Adresse
                  </label>
                  <input
                    type="text"
                    name="adresse_membre"
                    id="adresse_membre"
                    value={user.adresse_membre}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: Cité Universitaire J04"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label
                    htmlFor="date_naissance"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Date de naissance
                  </label>
                  <input
                    type="date"
                    name="date_naissance"
                    id="date_naissance"
                    value={user.date_naissance}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="cin_membre"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    CIN
                  </label>
                  <input
                    type="text"
                    name="cin_membre"
                    id="cin_membre"
                    value={user.cin_membre}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: 101 254 678 902"
                  />
                </div>
                
                <div>
                  <label
                    htmlFor="num_matricule"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    N° Matricule
                  </label>
                  <input
                    type="text"
                    name="num_matricule"
                    id="num_matricule"
                    value={user.num_matricule}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: 2023001"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="id_prom"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Promotion
                  </label>
                  <select
                    id="id_prom"
                    name="id_prom"
                    value={user.id_prom}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    disabled={promotionsLoading}
                  >
                    <option value="">Sélectionner une promotion</option>
                    {promotionsLoading ? (
                      <option value="" disabled>Chargement des promotions...</option>
                    ) : (
                      promotions.map(promotion => (
                        <option key={promotion.id_promotion} value={promotion.id_promotion}>
                          {promotion.name_promotion}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                
                <div>
                  <label
                    htmlFor="role_id"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Rôle *
                  </label>
                  <select
                    id="role_id"
                    name="role_id"
                    value={user.role_id}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    disabled={rolesLoading}
                  >
                    <option value="">Sélectionner un rôle</option>
                    {rolesLoading ? (
                      <option value="" disabled>Chargement des rôles...</option>
                    ) : (
                      roles.map(role => (
                        <option key={role.role_id} value={role.role_id}>
                          {role.role_name} {role.role_description && `(${role.role_description})`}
                        </option>
                      ))
                    )}
                  </select>
                  {validationErrors.role_id && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {validationErrors.role_id}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="md:col-span-1">
              <label
                htmlFor="photo_profile"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Photo de profil
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md h-[170px]">
                <div className="space-y-1 text-center">
                  {fileName ? (
                    <div className="flex flex-col items-center">
                      <MdPerson className="mx-auto h-12 w-12 text-indigo-500" />
                      <p className="text-sm text-gray-600">{fileName}</p>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="mt-2 inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <MdDelete className="mr-1 h-4 w-4" /> Supprimer
                      </button>
                    </div>
                  ) : (
                    <>
                      <MdCloudUpload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                        >
                          <span>Sélectionner une image</span>
                          <input 
                            id="file-upload" 
                            name="file-upload" 
                            type="file"
                            ref={fileInputRef}
                            accept="image/jpeg,image/png,image/gif,image/webp" 
                            onChange={handleFileChange}
                            className="sr-only" 
                          />
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF jusqu'à 2MB</p>
                    </>
                  )}
                </div>
              </div>
              {validationErrors.photo_profile && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.photo_profile}
                </p>
              )}
            </div>
          </div>
          
          <hr className="border-gray-200" />
          
          <h4 className="text-lg font-medium text-gray-700">Informations de connexion</h4>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Nom d'utilisateur *
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                  @
                </span>
                <input
                  type="text"
                  name="username"
                  id="username"
                  value={user.username}
                  onChange={handleInputChange}
                  className="w-full rounded-none rounded-r-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  placeholder="johndoe"
                />
              </div>
              {validationErrors.username && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.username}
                </p>
              )}
            </div>
            
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Adresse email *
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={user.email}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                placeholder="john@example.com"
              />
              {validationErrors.email && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.email}
                </p>
              )}
            </div>
            
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Mot de passe {!isEditing && "*"}
              </label>
              <input
                type="password"
                name="password"
                id="password"
                value={user.password}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                placeholder={isEditing ? "Laisser vide pour ne pas modifier" : "Mot de passe"}
              />
              {validationErrors.password && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.password}
                </p>
              )}
            </div>
          </div>

          {validationErrors.general && (
            <div className="rounded-md bg-red-50 p-3 border border-red-200">
              <p className="text-sm text-red-600">
                {validationErrors.general}
              </p>
            </div>
          )}

          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 rounded-md text-gray-600 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              onClick={onClose}
              disabled={submitting || loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm"
              disabled={submitting || loading}
            >
              {(submitting || loading) ? (
                <span className="flex items-center">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Chargement...
                </span>
              ) : isEditing ? (
                "Mettre à jour"
              ) : (
                "Créer"
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default UserFormModal;